import { ReactNode, useEffect, useRef, useState } from 'react'
import { Box, Fade, IconButton, Typography, alpha } from '@mui/material'
import { ZoomIn, ZoomOut, Close } from '@mui/icons-material'
import {
  TransformWrapper,
  TransformComponent,
  useControls
} from 'react-zoom-pan-pinch'
import useSteppedWheelZoom from './useSteppedWheelZoom'
import useImageAdjustments, {
  getAdjustmentFilter,
  getAdjustmentTransform
} from './useImageAdjustments'
import AdjustmentControls from './AdjustmentControls'
import { controlBarSx, controlButtonSx } from './constants'
import {
  IShortcutHint,
  ImageShortcutAction,
  getAriaKeyShortcuts,
  getShortcutTitle
} from './shortcuts'
import useSteppedZoomControls from './useSteppedZoomControls'

interface IZoomableImage {
  image: string
  alt: string
  minZoom?: number
  maxZoom?: number
  zoomStep?: number
  onZoomChange?: (zoomLevel: number) => void
  /** Camada absoluta sobre a imagem (mesmo box), que acompanha o zoom, o pan, o espelho e a rotação. */
  overlay?: ReactNode
  /** Mostra os controles de espelho, rotação, contraste e inversão de cores e liga os atalhos de teclado. */
  enableAdjustments?: boolean
  /** Atalhos tratados por quem usa o componente (ex.: navegar no modal), só para a lista de atalhos. */
  extraShortcuts?: IShortcutHint[]
}

interface IZoomControls {
  zoom: number
  isZoomedIn: boolean
  minZoom: number
  maxZoom: number
  zoomStep: number
  onWheelWithoutModifier: () => void
  /** Mostra a tecla no `title`; só quando os atalhos estão ligados. */
  hasShortcuts: boolean
}

const WHEEL_HINT_DURATION_MS = 1500

const shortcutProps = (action: ImageShortcutAction, hasShortcuts: boolean) =>
  hasShortcuts
    ? {
        title: getShortcutTitle(action),
        'aria-keyshortcuts': getAriaKeyShortcuts(action)
      }
    : {}

const ZoomControls: React.FC<IZoomControls> = ({
  zoom,
  isZoomedIn,
  minZoom,
  maxZoom,
  zoomStep,
  onWheelWithoutModifier,
  hasShortcuts
}) => {
  const { resetTransform } = useControls()
  const { zoomIn, zoomOut } = useSteppedZoomControls({
    step: zoomStep,
    minZoom,
    maxZoom
  })
  useSteppedWheelZoom({
    step: zoomStep,
    minZoom,
    maxZoom,
    onWheelWithoutModifier
  })

  return (
    <Box sx={controlBarSx}>
      <IconButton
        onClick={zoomOut}
        disabled={!isZoomedIn}
        size='small'
        sx={controlButtonSx}
        aria-label='Diminuir zoom'
        {...shortcutProps('zoomOut', hasShortcuts)}
      >
        <ZoomOut fontSize='small' />
      </IconButton>
      <Typography
        sx={{
          minWidth: '40px',
          textAlign: 'center',
          display: 'flex',
          alignItems: 'center',
          color: 'black',
          fontSize: '0.875rem',
          fontWeight: 500
        }}
      >
        {Math.round(zoom * 100)}%
      </Typography>
      <IconButton
        onClick={zoomIn}
        disabled={zoom >= maxZoom}
        size='small'
        sx={controlButtonSx}
        aria-label='Aumentar zoom'
        {...shortcutProps('zoomIn', hasShortcuts)}
      >
        <ZoomIn fontSize='small' />
      </IconButton>
      {isZoomedIn && (
        <IconButton
          onClick={() => resetTransform()}
          size='small'
          sx={{
            ...controlButtonSx,
            borderLeft: '1px solid rgba(0, 0, 0, 0.2)',
            borderRadius: 0
          }}
          title='Resetar zoom'
          aria-label='Resetar zoom'
        >
          <Close fontSize='small' />
        </IconButton>
      )}
    </Box>
  )
}

/** Preenche o pai (100% × 100%), que precisa ter altura definida: a imagem não dá altura ao componente. */
const ZoomableImage: React.FC<IZoomableImage> = ({
  image,
  alt,
  minZoom = 1,
  maxZoom = 4,
  zoomStep = 0.5,
  onZoomChange,
  overlay,
  enableAdjustments = false,
  extraShortcuts
}) => {
  const [zoom, setZoom] = useState(minZoom)
  const imageAdjustments = useImageAdjustments()
  const { adjustments } = imageAdjustments
  const isQuarterTurn =
    adjustments.rotation === 90 || adjustments.rotation === 270
  const [showWheelHint, setShowWheelHint] = useState(false)
  const wheelHintTimeout = useRef<ReturnType<typeof setTimeout>>()

  useEffect(() => () => clearTimeout(wheelHintTimeout.current), [])

  const showWheelHintBriefly = () => {
    setShowWheelHint(true)
    clearTimeout(wheelHintTimeout.current)
    wheelHintTimeout.current = setTimeout(
      () => setShowWheelHint(false),
      WHEEL_HINT_DURATION_MS
    )
  }

  const isZoomedIn = zoom > minZoom

  return (
    <Box
      sx={{
        position: 'relative',
        width: '100%',
        height: '100%',
        overflow: 'hidden',
        cursor: isZoomedIn ? 'grab' : 'default',
        '&:active': { cursor: isZoomedIn ? 'grabbing' : 'default' }
      }}
    >
      <TransformWrapper
        minScale={minZoom}
        maxScale={maxZoom}
        initialScale={minZoom}
        limitToBounds
        centerOnInit
        centerZoomedOut
        wheel={{ disabled: true }}
        // Em 100% não há o que deslocar; sem isso o toque de um dedo não rola a página.
        panning={{ disabled: !isZoomedIn }}
        doubleClick={{ mode: 'toggle', step: zoomStep * 2 }}
        onTransform={(_, state) => {
          // Arredonda para evitar ruído de ponto flutuante durante a animação
          const scale = Math.round(state.scale * 100) / 100
          if (scale === zoom) return
          setZoom(scale)
          onZoomChange?.(scale)
        }}
      >
        <TransformComponent
          wrapperStyle={{ width: '100%', height: '100%' }}
          contentStyle={{
            width: '100%',
            height: '100%',
            position: 'relative',
            containerType: 'size'
          }}
        >
          {/* Girado 90°/270°, o box troca largura e altura do container (cqh/cqw) para a imagem
              continuar encaixada inteira, sem medir o container. */}
          <Box
            data-testid='zoomable-image-transform'
            sx={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              width: isQuarterTurn ? '100cqh' : '100%',
              height: isQuarterTurn ? '100cqw' : '100%',
              transform:
                `translate(-50%, -50%) ${getAdjustmentTransform(adjustments)}`.trim()
            }}
          >
            <Box
              component='img'
              src={image}
              alt={alt}
              draggable={false}
              sx={{
                display: 'block',
                width: '100%',
                height: '100%',
                objectFit: 'contain',
                userSelect: 'none',
                filter: getAdjustmentFilter(adjustments)
              }}
            />
            {overlay}
          </Box>
        </TransformComponent>
        <Box
          sx={{
            position: 'absolute',
            top: 8,
            left: 8,
            right: 8,
            display: 'flex',
            flexWrap: 'wrap',
            alignItems: 'flex-start',
            gap: 0.5,
            zIndex: 5,
            pointerEvents: 'none',
            '& > *': { pointerEvents: 'auto' }
          }}
        >
          <ZoomControls
            zoom={zoom}
            isZoomedIn={isZoomedIn}
            minZoom={minZoom}
            maxZoom={maxZoom}
            zoomStep={zoomStep}
            onWheelWithoutModifier={showWheelHintBriefly}
            hasShortcuts={enableAdjustments}
          />
          {enableAdjustments && (
            <AdjustmentControls
              {...imageAdjustments}
              isZoomedIn={isZoomedIn}
              zoomStep={zoomStep}
              minZoom={minZoom}
              maxZoom={maxZoom}
              extraShortcuts={extraShortcuts}
            />
          )}
        </Box>
      </TransformWrapper>
      <Box
        role='status'
        sx={{
          position: 'absolute',
          inset: 0,
          pointerEvents: 'none',
          zIndex: 6
        }}
      >
        <Fade in={showWheelHint} unmountOnExit>
          <Box
            sx={{
              width: '100%',
              height: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              bgcolor: theme => alpha(theme.palette.common.black, 0.4)
            }}
          >
            <Typography
              sx={{
                px: 2,
                py: 1,
                borderRadius: 1,
                color: 'common.white',
                bgcolor: theme => alpha(theme.palette.common.black, 0.7)
              }}
            >
              Use Ctrl + roda do mouse para dar zoom
            </Typography>
          </Box>
        </Fade>
      </Box>
    </Box>
  )
}

export default ZoomableImage
